import { ruleSetsApi } from '@/api/ruleSets.api';
import { useToolSystem } from '@/context/ToolSystemContext';
import type { RuleSetListItem } from '@/types/ruleSet.types';
import type { ToolSystem } from '@/types/toolSystem.types';
import { useCallback, useEffect, useState } from 'react';

/**
 * Load the hole rule sets a project can be machined with, in the active tool system.
 *
 * Only that system's sets: a set pins tools by name and machine number, so one written
 * against the other catalog is not something this project could be machined with.
 * `forToolSystem` overrides the app-wide choice for a page about one particular project,
 * which stays in its own system whatever the header switch says.
 *
 * Deliberately silent on failure — the caller renders the list inside one card of a page whose
 * main content loaded fine, so a failed fetch is reported in place rather than as a toast.
 */
export const useRuleSets = (forToolSystem?: ToolSystem | null) => {
  const { toolSystem: activeToolSystem } = useToolSystem();
  const toolSystem = forToolSystem ?? activeToolSystem;
  const [ruleSets, setRuleSets] = useState<RuleSetListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRuleSets = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setRuleSets(await ruleSetsApi.getAll(toolSystem));
    } catch (err) {
      console.error('Failed to load the hole rule sets:', err);
      setError('ruleSets.loadFailed');
    } finally {
      setIsLoading(false);
    }
  }, [toolSystem]);

  useEffect(() => {
    fetchRuleSets();
  }, [fetchRuleSets]);

  return { ruleSets, isLoading, error, refetch: fetchRuleSets };
};
